// The lens-agnostic kanta/salkku overview grammar every perspective with an overview mounts:
// situation cards → "Kiinteistöt" tile grid → map. Rajaa lives in the topbar
// (optimization status facet hidden on these overviews). Konfiguraatio fills it with setup
// questions (`useConfigSetupLens`); the three judgment
// lenses fill it from their scoped `Finding[]` (`useLensFindingsOverview`, story #1296 task
// #1308) — both produce an `OverviewLensState` and this component renders it identically
// (docs/spec/concepts.md §2.4 → "Portfolio-scope overview grammar"). No fetching of its own:
// the caller owns the state hook, this component only renders it.
//
// The one piece of interaction owned here: a situation card doubles as a facet selector. Picking
// a non-zero card narrows the tile grid and the map to that bucket's building set, in place —
// never a navigation to Havainnot/Tapahtumaloki, never written to the URL or inventory scope. The
// facet is page-local React state, reset whenever the scope itself changes (salkku, city/type
// filter, or the underlying scoped building set) so it can never silently outlive the scope it
// was computed against.
import { useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";

import { OverviewSituationCards } from "@/components/control-tower/perspectives/OverviewSituationCards";
import { OverviewBuildingList } from "@/components/control-tower/perspectives/OverviewBuildingList";
import { OverviewMap } from "@/components/control-tower/perspectives/OverviewMap";
import { SectionPanel } from "@/components/ui/section-panel";
import { useInventoryFilter } from "@/lib/inventory-filter";
import {
  filterBuildingsByFacet,
  type OverviewCopy,
  type OverviewLensState,
} from "@/lib/overview-lens";

export function PerspectiveOverview({
  state,
  copy,
}: {
  state: OverviewLensState;
  copy: OverviewCopy;
}) {
  const { groupId, filter } = useInventoryFilter();
  const [facetKey, setFacetKey] = useState<string | null>(null);

  const scopeResetKey = [
    groupId ?? "",
    filter.city,
    filter.type,
    filter.status,
    state.scopedBuildings
      .map((b) => b.id)
      .sort()
      .join(","),
  ].join("|");
  useEffect(() => {
    setFacetKey(null);
  }, [scopeResetKey]);

  const narrowedBuildings = useMemo(
    () =>
      filterBuildingsByFacet(
        state.scopedBuildings,
        state.perBuilding,
        facetKey,
      ),
    [state.scopedBuildings, state.perBuilding, facetKey],
  );
  const activeBucket = state.buckets.find((b) => b.key === facetKey) ?? null;

  return (
    <>
      <OverviewSituationCards
        buckets={state.buckets}
        totalCount={state.totalCount}
        loading={state.loading && state.settledCount === 0}
        copy={copy}
        selectedKey={facetKey}
        onSelect={(key) =>
          setFacetKey((current) => (current === key ? null : key))
        }
      />

      <SectionPanel
        icon={Building2}
        title="Kiinteistöt"
        subtitle={
          activeBucket
            ? `Rajattu: ${activeBucket.label} (${activeBucket.count})`
            : undefined
        }
        actions={
          facetKey !== null ? (
            <button
              type="button"
              onClick={() => setFacetKey(null)}
              className="text-[11px] font-medium text-primary hover:underline"
            >
              Näytä kaikki
            </button>
          ) : undefined
        }
      >
        <OverviewBuildingList
          scopedBuildings={narrowedBuildings}
          perBuilding={state.perBuilding}
          completeLabel={copy.tileCompleteLabel}
          buckets={state.buckets}
        />
      </SectionPanel>

      <OverviewMap
        scopedBuildings={narrowedBuildings}
        perBuilding={state.perBuilding}
        totalBuildings={narrowedBuildings.length}
        loading={state.loading}
        copy={copy}
      />
    </>
  );
}
